import { watch } from 'vue';
import { useUserStore } from '~/stores/user';

/**
 * Owns "this account's origin IP has been recorded" at app level.
 *
 * Deliberately the same shape as plugins/avatar.client.js, and for the same
 * reason: watching the store rather than hooking a route covers EVERY auth path
 * — password login's hard reload, the OAuth redirect, a guest session, and
 * plain session restore — regardless of which route the user lands on.
 *
 * That breadth is the point here. Guests and OAuth members are created by
 * GoTrue directly, with no edge function of ours in the loop, so there is no
 * server-side moment to hang this off; only `sign-up` (email members) can stamp
 * the row inline. A watcher is what reaches the other two.
 *
 * Fire and forget: nothing renders from this, and a guest must get their
 * session whether or not the call succeeds. ensureSessionIp de-duplicates
 * against its own flag, and the function behind it only writes when the column
 * is still NULL, so firing on every auth transition is safe.
 */
export default defineNuxtPlugin((nuxtApp) => {
  // Pass the pinia instance explicitly rather than relying on the active one:
  // plugins run outside a component setup, so there is no ambient instance to
  // fall back on if module plugin ordering ever changes.
  const userStore = useUserStore(nuxtApp.$pinia);

  watch(
    () => userStore.userProfile?.id,
    (id) => {
      if (!id) return;
      // runWithContext because this watcher lives on the plugin, not a
      // component: when it fires there may be no active Nuxt instance, and
      // ensureSessionIp reaches useNuxtApp(), which throws without one.
      nuxtApp.runWithContext(() => userStore.ensureSessionIp());
    },
    { immediate: true }
  );
});
